/**
 * platform/staffSession.ts — signing in AS A PERSON (owner decision D6).
 *
 * Identity Platform password, then a mandatory authenticator code, then a
 * `pcs_` session token. The token goes in exactly the slot the per-operator
 * secret uses today (`PlatformActor.systemToken`), with the operator's own
 * email as the attribution the server cross-checks — so every console request
 * path works unchanged. Same rules as the secret: memory only, never stored,
 * `credentials: 'omit'`, absolute URL.
 *
 * A 404 from sign-in means the environment has not enabled individual sign-in
 * (PLATFORM_OPERATOR_AUTH=secret). That is said plainly, not shown as an error.
 */
import type { ConsoleRequest } from './requests.js';

export const STAFF_BASE = '/api/system/staff';

export const staffRequests = {
  signIn: (email: string, password: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/sign-in`,
    body: { email, password },
  }),
  mfa: (challenge: string, code: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/sign-in/mfa`,
    body: { challenge, code },
  }),
  signOut: (): ConsoleRequest => ({ method: 'POST', path: `${STAFF_BASE}/sign-out` }),
  me: (): ConsoleRequest => ({ method: 'GET', path: `${STAFF_BASE}/me` }),
  directory: (): ConsoleRequest => ({ method: 'GET', path: `${STAFF_BASE}/directory` }),
  invitations: (): ConsoleRequest => ({ method: 'GET', path: `${STAFF_BASE}/invitations` }),
  invite: (email: string, role: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/invitations`,
    body: { email, role },
  }),
  revokeInvitation: (id: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/invitations/${encodeURIComponent(id)}/revoke`,
  }),
  resetInvitationAddress: (id: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/invitations/${encodeURIComponent(id)}/reset-address`,
  }),
  changeMember: (id: string, change: { status: string } | { role: string }): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/members/${encodeURIComponent(id)}`,
    body: change,
  }),
  activity: (): ConsoleRequest => ({ method: 'GET', path: `${STAFF_BASE}/activity` }),
  repin: (id: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/members/${encodeURIComponent(id)}/repin`,
  }),
  acceptGoogleIntent: (token: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/invitations/accept/google/intent`,
    body: { token },
  }),
  acceptGoogleContinue: (state: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/invitations/accept/google/continue`,
    body: { state },
  }),
  revokeSessions: (id: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/members/${encodeURIComponent(id)}/revoke-sessions`,
  }),
  acceptStart: (
    token: string,
    email: string,
    password: string,
    createAccount: boolean,
  ): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/invitations/accept/start`,
    body: { token, email, password, createAccount },
  }),
  acceptEnroll: (enrollment: string, code: string, displayName: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/invitations/accept/enroll`,
    body: { enrollment, code, displayName },
  }),
  acceptVerify: (challenge: string, code: string, displayName: string): ConsoleRequest => ({
    method: 'POST',
    path: `${STAFF_BASE}/invitations/accept/verify`,
    body: { challenge, code, displayName },
  }),
};

// ---------------------------------------------------------------------------
// Staff administration shapes (`platform:staff.administer`).
// ---------------------------------------------------------------------------
export interface StaffMember {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: string;
  readonly capabilities: readonly string[];
  readonly status: 'active' | 'suspended' | 'deactivated';
  readonly createdAt: string;
  readonly invitedBy: string | null;
  readonly lastSignedInAt: string | null;
  readonly activeSessions: number;
}
export interface StaffRoleProfile {
  readonly id: string;
  readonly label: string;
  readonly capabilities: readonly string[];
}
export interface StaffDirectory {
  readonly staff: readonly StaffMember[];
  readonly roles: readonly StaffRoleProfile[];
}
export interface StaffInvitation {
  readonly id: string;
  readonly email: string;
  readonly role: string;
  readonly kind: 'invitation' | 'bootstrap';
  readonly status: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly invitedBy: string | null;
}
export interface InvitationCreated {
  readonly invitationId: string;
  readonly expiresAt: string;
  readonly resent: boolean;
  readonly acceptPath: string;
  readonly delivery: 'manual';
  /** Set on a re-pin link: the link pins a NEW authenticator for an existing member. */
  readonly repin?: boolean;
  /** Re-pin only: false when Identity Platform could not clear the old factor. */
  readonly identityPlatformFactorsCleared?: boolean;
}

// ---------------------------------------------------------------------------
// Invitation acceptance (no session; the token is the credential).
// ---------------------------------------------------------------------------
export type AcceptStep =
  | { kind: 'enroll'; enrollment: string; secret: string; otpauthUri: string }
  | { kind: 'verify'; challenge: string }
  | { kind: 'accepted'; role: string }
  | { kind: 'refused'; code: string; message: string }
  | { kind: 'not_enabled' }
  | { kind: 'transport'; message: string };

export async function acceptCall(
  baseUrl: string,
  request: ConsoleRequest,
  send: Send = (i, init) => fetch(i, init),
): Promise<AcceptStep> {
  let res: Response;
  try {
    res = await send(`${baseUrl}${request.path}`, {
      method: request.method,
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(request.body),
    });
  } catch (err) {
    return { kind: 'transport', message: err instanceof Error ? err.message : 'unreachable' };
  }
  if (res.status === 404) return { kind: 'not_enabled' };
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.ok && body.step === 'enroll') {
    return {
      kind: 'enroll',
      enrollment: String(body.enrollment),
      secret: String(body.secret),
      otpauthUri: String(body.otpauthUri),
    };
  }
  if (res.ok && body.step === 'verify')
    return { kind: 'verify', challenge: String(body.challenge) };
  if (res.ok && body.accepted === true) return { kind: 'accepted', role: String(body.role) };
  return {
    kind: 'refused',
    code: typeof body.code === 'string' ? body.code : 'refused',
    message: typeof body.message === 'string' ? body.message : `Refused (${res.status}).`,
  };
}

/** Every staff request shape — the fixture the nginx binding test walks. */
export function allStaffRequestShapes(): ConsoleRequest[] {
  return [
    staffRequests.signIn('a@b.c', 'x'),
    staffRequests.mfa('c', '000000'),
    staffRequests.signOut(),
    staffRequests.me(),
  ];
}

export const isStaffToken = (token: string): boolean => token.startsWith('pcs_');

export type SignInStep =
  | { kind: 'challenge'; challenge: string }
  | { kind: 'signed_in'; token: string; email: string; expiresAt: string }
  | { kind: 'mfa_enrollment_required'; message: string }
  | { kind: 'refused'; message: string }
  | { kind: 'not_enabled' }
  | { kind: 'transport'; message: string };

type Send = (input: string, init: RequestInit) => Promise<Response>;

async function post(
  baseUrl: string,
  request: ConsoleRequest,
  send: Send,
): Promise<SignInStep | Response> {
  try {
    return await send(`${baseUrl}${request.path}`, {
      method: request.method,
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(request.body),
    });
  } catch (err) {
    return { kind: 'transport', message: err instanceof Error ? err.message : 'unreachable' };
  }
}

async function interpret(res: SignInStep | Response): Promise<SignInStep> {
  if (!(res instanceof Response)) return res;
  if (res.status === 404) return { kind: 'not_enabled' };
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.ok && typeof body.challenge === 'string')
    return { kind: 'challenge', challenge: body.challenge };
  if (res.ok && typeof body.token === 'string') {
    const staff = body.staff as { email?: string } | undefined;
    return {
      kind: 'signed_in',
      token: body.token,
      email: staff?.email ?? '',
      expiresAt: String(body.expiresAt ?? ''),
    };
  }
  const message = typeof body.message === 'string' ? body.message : `Refused (${res.status}).`;
  if (body.code === 'mfa_enrollment_required') return { kind: 'mfa_enrollment_required', message };
  return { kind: 'refused', message };
}

export async function signIn(
  baseUrl: string,
  email: string,
  password: string,
  send: Send = (i, init) => fetch(i, init),
): Promise<SignInStep> {
  return interpret(await post(baseUrl, staffRequests.signIn(email, password), send));
}

export async function verifyCode(
  baseUrl: string,
  challenge: string,
  code: string,
  send: Send = (i, init) => fetch(i, init),
): Promise<SignInStep> {
  return interpret(await post(baseUrl, staffRequests.mfa(challenge, code), send));
}

// ---------------------------------------------------------------------------
// GOOGLE AS THE FIRST FACTOR (owner decision 2026-10-08). The browser leaves
// for Google by a top-level navigation to a RELATIVE path, so the flow cookie
// and the callback share the Console's own origin (nginx proxies
// /api/system/staff/ to the API). Google never yields a session: the callback
// returns a sealed challenge (or, for an invitation, a sealed result) in the
// URL FRAGMENT, and the authenticator step follows exactly as for a password.
// ---------------------------------------------------------------------------
export const GOOGLE_START_PATH = `${STAFF_BASE}/google/start`;

export type GoogleReturn =
  | { kind: 'challenge'; challenge: string }
  | { kind: 'accept'; state: string }
  | { kind: 'error'; reason: string };

/** What the Google callback put in the fragment, if anything. Never a session. */
export function readGoogleReturn(hash: string): GoogleReturn | null {
  const m = /^#?google-(challenge|accept|error)=(.+)$/.exec(hash);
  if (!m) return null;
  const value = decodeURIComponent(m[2]!);
  if (m[1] === 'challenge') return { kind: 'challenge', challenge: value };
  if (m[1] === 'accept') return { kind: 'accept', state: value };
  return { kind: 'error', reason: value };
}

export const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  sign_in_failed:
    'Google sign-in did not lead to Console access. Your Google account must be the one you ' +
    'were invited with, and it must have an authenticator app.',
  provider_unavailable: 'Sign-in is temporarily unavailable. Try again shortly.',
  invitation_invalid: 'This invitation link is no longer valid. Ask for a new one.',
};

/** Bind an invitation to a sealed, short-lived Google intent. */
export async function googleIntent(
  baseUrl: string,
  token: string,
  send: Send = (i, init) => fetch(i, init),
): Promise<string | null> {
  try {
    const req = staffRequests.acceptGoogleIntent(token);
    const res = await send(`${baseUrl}${req.path}`, {
      method: req.method,
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(req.body),
    });
    if (!res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as { intent?: unknown };
    return typeof body.intent === 'string' ? body.intent : null;
  } catch {
    return null;
  }
}
